import { useEffect, useRef, useState } from 'react'

import Link from 'next/link'

import { type Menu as MenuType, type SubMenu as SubMenuType } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'

import 'rc-menu/assets/index.css'

import { useNotification } from '@/containers'
import ImageOptimize from '../ImageOptimize'

interface CateDropdownProps {
  items: MenuType[]
  showIcon?: boolean
  className?: string
  popupClassName?: string
}

const CateDropdown = ({ items, className }: CateDropdownProps) => {
  const ref = useRef<HTMLInputElement>(null)
  const [childMegaMenu, setChildMegaMenu] = useState(items[0]?.children || [])
  const [selectedItem, setSelectedItem] = useState(items[0])
  const [height, setHeight] = useState<number | null>(null)
  const [width, setWidth] = useState<number | null>(null)
  const { systemConfig } = useNotification()

  useEffect(() => {
    const divW = document.querySelector('.headerMenu')

    if (divW instanceof HTMLDivElement) {
      setWidth(divW.offsetWidth)
    }
  }, [])
  useEffect(() => {
    const div = document.querySelector('.mgLeftArea')

    if (div instanceof HTMLDivElement) {
      setHeight(div.offsetHeight)
    }
  }, [])

  const checkMenuItemType = (e: MenuType | SubMenuType) => {
    switch (e?.type) {
      case 'productCat':
        return e?.slug + '.c' || '/'
      case 'link':
        return e?.url || '/'
      case 'post':
        return e?.slug + '.b' || '/'
      case 'postCat':
        return (e?.slug as string) || '/'
      case 'product':
        return (e?.slug as string) || '/'
      default:
        return e?.url || '/'
    }
  }

  return (
    <div className={`${className}`} style={{ minWidth: `${width}px` }} ref={ref}>
      <div className='z-20 flex w-full items-stretch bg-white'>
        <div className='h-full max-w-[248px] flex-[0_0_248px] border-r-[6px] border-white-700'>
          <div className='mgLeftArea flex flex-col border-b border-grey-100 pb-5'>
            {items &&
              items.map((e, index) => (
                <div
                  key={index + e.menuType}
                  className={`relative flex w-full items-center px-3 py-[12px] hover:bg-pink-200 ${
                    e._id == selectedItem._id ? 'bg-pink-200' : ''
                  }`}
                  onMouseEnter={() => {
                    setChildMegaMenu(e?.children)
                    setSelectedItem(e)
                  }}
                >
                  <div className='max-w-4 flex-[0_0_24px]'>
                    {e.image ? (
                      <div className='absolute left-[-8px] top-[50%] translate-y-[-50%]'>
                        <ImageOptimize src={e.image} width={54} height={34} alt={getTranslate(e.name)} priority />
                      </div>
                    ) : (
                      <ImageOptimize src={e.icon} width={24} height={24} alt={getTranslate(e.name)} priority />
                    )}
                  </div>
                  <div className='max-w-[calc(100%-24px)] flex-[0_0_calc(100%-24px)] pl-2'>
                    <Link
                      href={checkMenuItemType(e)}
                      className={`cursor-pointer text-base font-semibold text-black-400 transition-all ease-in`}
                    >
                      {getTranslate(e.name)}
                    </Link>
                  </div>
                </div>
              ))}
          </div>
          <div className='flex flex-col gap-3 px-3 py-5'>
            <Link href={systemConfig?.warehousePostPolicy || '/'} className='flex gap-1.5 text-base text-black-400'>
              Chính sách
            </Link>
            <Link href={'/post'} className='flex gap-1.5 text-base text-black-400'>
              Tin tức & Khuyến mãi
            </Link>
          </div>
        </div>
        <div className='flex-1 flex-wrap overflow-y-auto p-1'>
          <div className='w-full gap-x-10 gap-y-8 px-3 py-2'>
            <Link href={checkMenuItemType(childMegaMenu[0])} key={'lv-2-1'}>
              <h6 className='mb-4 flex gap-1.5 text-base font-bold text-black-400'>
                {getTranslate(childMegaMenu[0]?.name)}
              </h6>
            </Link>
            <div className='grid grid-cols-6 gap-3'>
              {childMegaMenu[0]?.children &&
                childMegaMenu[0]?.children?.slice(0, 12).map((f, fIndex: number) => (
                  <a
                    href={checkMenuItemType(f)}
                    key={'lv-3-' + fIndex}
                    className='flex flex-wrap justify-center text-center'
                  >
                    <ImageOptimize
                      width={64}
                      height={64}
                      alt={getTranslate(f?.name) || ''}
                      src={f?.icon}
                      className='mb-1 h-16 w-16'
                      priority
                    />
                    <p className='line-clamp-2 h-10 w-full cursor-pointer text-center text-sm leading-5 text-black-400 hover:text-blue-400'>
                      {getTranslate(f.name)}
                    </p>
                  </a>
                ))}
            </div>
          </div>
          <div
            className='grid w-full grid-cols-4 gap-x-2 gap-y-8 px-3 py-2'
            style={{ maxHeight: `${(height as number) - 16}px` }}
          >
            {childMegaMenu &&
              childMegaMenu.slice(1, 5).map((r, ind) => (
                <div key={ind} className='w-full'>
                  <Link href={checkMenuItemType(r)} key={'lv-2-' + ind}>
                    <span className='mb-1 flex gap-1.5 text-sm font-bold text-black-400'>{getTranslate(r?.name)}</span>
                  </Link>
                  <div className='flex flex-col'>
                    {r?.children &&
                      r?.children.slice(0, 3).map((f, fIndex: number) => (
                        <Link href={checkMenuItemType(f)} key={'lv-3-' + fIndex}>
                          <span className='mb-2 cursor-pointer text-mainBody font-normal !leading-[20px] text-black-400 hover:text-blue-400'>
                            {getTranslate(f.name)}
                          </span>
                        </Link>
                      ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CateDropdown
